import React from 'react';
import { Smartphone, RefreshCcw, FolderKanban, Brain, Sparkles, Zap, Shield, TrendingUp } from 'lucide-react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';

const serviceItems = [
  {
    icon: <Brain className="w-8 h-8" />,
    title: "KI-Integration",
    subtitle: "Next-Level Automation",
    description: "Transformiere dein Business mit intelligenter KI, die mitdenkt und vorausschaut.",
    features: [
      { icon: <Zap className="w-4 h-4" />, text: "80% weniger Routinearbeit" },
      { icon: <TrendingUp className="w-4 h-4" />, text: "3+ Stunden täglich gespart" },
      { icon: <Shield className="w-4 h-4" />, text: "24/7 KI-Assistenz" }
    ],
    gradient: "from-purple-600 to-pink-600",
    delay: 0
  },
  {
    icon: <Smartphone className="w-8 h-8" />,
    title: "Premium Apps",
    subtitle: "Swiss Quality Code",
    description: "Massgeschneiderte Apps, die begeistern - mit Liebe zum Detail entwickelt.",
    features: [
      { icon: <Sparkles className="w-4 h-4" />, text: "Ultra-intuitive UX" },
      { icon: <Shield className="w-4 h-4" />, text: "100% Swiss Made" },
      { icon: <Zap className="w-4 h-4" />, text: "Blitzschnelle Performance" }
    ],
    gradient: "from-blue-600 to-cyan-600",
    delay: 0.1
  },
  {
    icon: <RefreshCcw className="w-8 h-8" />,
    title: "Automatisierung",
    subtitle: "Work Smarter",
    description: "Prozesse, die wie von selbst laufen - mehr Zeit für das Wesentliche.",
    features: [
      { icon: <TrendingUp className="w-4 h-4" />, text: "Effizienz +200%" },
      { icon: <Zap className="w-4 h-4" />, text: "Zero-Touch Workflows" },
      { icon: <Shield className="w-4 h-4" />, text: "Fehlerquote -95%" }
    ],
    gradient: "from-orange-600 to-red-600",
    delay: 0.2
  },
  {
    icon: <FolderKanban className="w-8 h-8" />,
    title: "Digital Excellence",
    subtitle: "Future Ready",
    description: "Digitale Transformation, die dein Unternehmen an die Spitze bringt.",
    features: [
      { icon: <Sparkles className="w-4 h-4" />, text: "Cutting-Edge Tech" },
      { icon: <TrendingUp className="w-4 h-4" />, text: "Skalierbar & Flexibel" },
      { icon: <Shield className="w-4 h-4" />, text: "Zukunftssicher" }
    ],
    gradient: "from-green-600 to-emerald-600",
    delay: 0.3
  }
];

const Services: React.FC = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  return (
    <section id="services" className="py-32 bg-gradient-to-b from-white via-gray-50 to-white relative overflow-hidden">
      <div className="absolute inset-0 opacity-30">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-100/30 to-blue-100/30"></div>
      </div>
      
      <div className="container mx-auto px-4 relative z-10">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={inView ? { scale: 1 } : {}}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center justify-center px-6 py-2 mb-6 text-sm font-semibold text-purple-700 bg-purple-100 rounded-full"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            SWISS EXCELLENCE IN DIGITAL
          </motion.div>
          
          <h2 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6">
            <span className="block text-gray-900">Nicht nur Code.</span>
            <span className="block bg-clip-text text-transparent bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 pb-2 leading-tight">
              Digitale Magie.
            </span>
          </h2>
          
          <p className="text-xl md:text-2xl text-gray-600 max-w-3xl mx-auto">
            Ich erschaffe digitale Erlebnisse, die deine Konkurrenz alt aussehen lassen.
            <span className="block mt-2 text-lg text-gray-500">
              Jeden Tag. Messbar. Beeindruckend.
            </span>
          </p>
        </motion.div>
        
        <motion.div 
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
        >
          {serviceItems.map((service, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: service.delay }}
              whileHover={{ y: -10, transition: { duration: 0.2 } }}
              className="group relative"
            >
              <div className="relative h-full bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br opacity-10 rounded-full -translate-y-1/2 translate-x-1/2 group-hover:scale-150 transition-transform duration-500"
                     style={{
                       background: `linear-gradient(to bottom right, var(--tw-gradient-stops))`,
                       '--tw-gradient-from': service.gradient.split(' ')[1],
                       '--tw-gradient-to': service.gradient.split(' ')[3],
                     } as React.CSSProperties}
                />
                
                <motion.div 
                  className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br ${service.gradient} text-white mb-6 group-hover:scale-110 transition-transform duration-300`}
                  whileHover={{ rotate: 360 }}
                  transition={{ duration: 0.6 }}
                >
                  {service.icon}
                </motion.div>
                
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{service.title}</h3>
                <p className="text-sm font-semibold text-transparent bg-clip-text bg-gradient-to-r"
                   style={{
                     backgroundImage: `linear-gradient(to right, var(--tw-gradient-stops))`,
                     '--tw-gradient-from': service.gradient.split(' ')[1],
                     '--tw-gradient-to': service.gradient.split(' ')[3],
                   } as React.CSSProperties}
                >
                  {service.subtitle}
                </p>
                
                <p className="text-gray-600 mt-4 mb-6">{service.description}</p>
                
                <div className="space-y-3">
                  {service.features.map((feature, idx) => (
                    <motion.div 
                      key={idx}
                      initial={{ opacity: 0, x: -20 }}
                      animate={inView ? { opacity: 1, x: 0 } : {}}
                      transition={{ duration: 0.5, delay: service.delay + idx * 0.1 }}
                      className="flex items-center text-sm text-gray-700"
                    >
                      <span className={`mr-3 text-transparent bg-clip-text bg-gradient-to-r ${service.gradient}`}>
                        {feature.icon}
                      </span>
                      {feature.text}
                    </motion.div>
                  ))}
                </div>
                
                <motion.div
                  className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  style={{
                    background: `linear-gradient(to right, var(--tw-gradient-stops))`,
                    '--tw-gradient-from': service.gradient.split(' ')[1],
                    '--tw-gradient-to': service.gradient.split(' ')[3],
                  } as React.CSSProperties}
                  initial={{ scaleX: 0 }}
                  whileHover={{ scaleX: 1 }}
                  transition={{ duration: 0.3 }}
                />
              </div>
            </motion.div>
          ))}
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="mt-20 text-center"
        >
          <Link to="/termin-buchen">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="group relative inline-flex items-center px-8 py-4 text-lg font-semibold text-white bg-gradient-to-r from-blue-600 to-purple-600 rounded-full overflow-hidden transition-all duration-300 hover:shadow-2xl"
            >
            <span className="relative z-10">Lass uns dein Projekt starten</span>
            <Sparkles className="ml-2 w-5 h-5 relative z-10 group-hover:rotate-180 transition-transform duration-500" />
            <motion.div
              className="absolute inset-0 bg-gradient-to-r from-purple-600 to-pink-600"
              initial={{ x: "100%" }}
              whileHover={{ x: 0 }}
              transition={{ duration: 0.3 }}
            />
            </motion.button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default Services;